/**
 * Collection Types
 *
 * Collections are the permission boundary for blocks. Every block belongs to
 * exactly one collection, and access is controlled via CollectionMember rows
 * with role-based permissions (owner | editor | viewer).
 *
 * All types are defined as Zod schemas with inferred TypeScript types.
 * This provides runtime validation while keeping full type inference.
 */
import { z } from "zod";
import { prefixedIdSchema } from "./id";
// ---------------------------------------------------------------------------
// Collection Type
// ---------------------------------------------------------------------------
export const CollectionTypeSchema = z.enum(["primary", "private", "shared"]);
// ---------------------------------------------------------------------------
// Collection Member Role
// ---------------------------------------------------------------------------
export const CollectionMemberRoleSchema = z.enum(["owner", "editor", "viewer"]);
// ---------------------------------------------------------------------------
// Layer Index
// ---------------------------------------------------------------------------
// Taxonomic class of a collection in the hierarchy. DB stores raw integers so
// future layers can be added without migration. Semantic mapping today:
//   -1: future "supergroup"
//    0: group  (the only layer that accepts direct CollectionMember rows)
//    1: collection (default)
//    2: stack
//
// Invariants enforced at the entity layer:
//   - `type = "primary"` always implies `layerIndex = 0`. Primary is just a
//     non-shareable special case of Group.
//   - Every layerIndex >= 1 row must have a non-NULL parentCollectionId
//     (i.e. every Collection/Stack lives under a Group).
//   - Direct CollectionMember rows are only legal at `layerIndex === 0`.
//     Layers 1 and 2 inherit access via `effective_collection_role`.
//
// Wide bounds with room to grow; see security-limits rule.
// 0 index is Group
// 1 index is Collection
// 2 index is Stack
// ... we can eventually expand if we need
export const MIN_LAYER_INDEX = 0;
export const MAX_LAYER_INDEX = 2;
/**
 * Named layer index constants. Use these instead of magic numbers when
 * filtering or asserting a collection's place in the hierarchy.
 *
 * Adding a new layer here must stay in sync with the comment block above
 * and any documentation that enumerates the taxonomic classes.
 */
export const COLLECTION_LAYER = {
    group: 0,
    collection: 1,
    stack: 2,
};
/**
 * Legal parent-child layer pairs in the Group/Collection/Stack hierarchy.
 *
 * The tree shape is deliberately decoupled from a strict
 * `child.layerIndex = parent.layerIndex + 1` cascade so that a Stack
 * (layer 2) can sit directly under a Group (layer 0) — not just under
 * a layer-1 Collection. This supports "stack things together right
 * here in my Group / homepage feed" without forcing the user to first
 * stage a Collection.
 *
 * Allowed pairs (parent → child):
 *   - Group (0)      → Collection (1)   — the canonical workspace tree
 *   - Group (0)      → Stack (2)        — flat workspace, no Collection layer
 *   - Collection (1) → Stack (2)        — Stacks inside a Collection
 *
 * `Math.abs(parent - child) === 1` is no longer the rule. Direct parent-child
 * relationships are defined by this set; `effective_collection_role`'s
 * upward walk handles ACL inheritance regardless of layer distance.
 */
export const LEGAL_PARENT_CHILD_LAYER_PAIRS = [
    [COLLECTION_LAYER.group, COLLECTION_LAYER.collection],
    [COLLECTION_LAYER.group, COLLECTION_LAYER.stack],
    [COLLECTION_LAYER.collection, COLLECTION_LAYER.stack],
];
/**
 * True iff the given (parent, child) layer indices form a legal direct
 * parent-child edge in the hierarchy. See `LEGAL_PARENT_CHILD_LAYER_PAIRS`.
 */
export function isLegalParentChildLayers(parentLayer, childLayer) {
    for (const [p, c] of LEGAL_PARENT_CHILD_LAYER_PAIRS) {
        if (p === parentLayer && c === childLayer)
            return true;
    }
    return false;
}
// ---------------------------------------------------------------------------
// Collection Metadata
// ---------------------------------------------------------------------------
export const CollectionMetadataSchema = z.object({
    color: z.string().max(50).optional(),
    icon: z.string().max(50).optional(),
    emoji: z.string().max(10).optional(),
    imageUrl: z.string().url().max(2000).optional(),
});
// ---------------------------------------------------------------------------
// Collection Schema
// ---------------------------------------------------------------------------
export const CollectionSchema = z.object({
    id: prefixedIdSchema("collection"),
    ownerId: z.string(),
    type: CollectionTypeSchema,
    layerIndex: z.number().int().min(MIN_LAYER_INDEX).max(MAX_LAYER_INDEX).default(1),
    parentCollectionId: prefixedIdSchema("collection").optional(),
    isImplicit: z.boolean().default(false),
    title: z.string().max(200).optional(),
    summary: z.string().max(1000).optional(),
    metadata: CollectionMetadataSchema,
    /**
     * URL-safe handle. Powers `putty.me/{name}` (layer-0 group slugs share
     * the global root namespace with `Account.username`) and
     * `putty.me/{name}/{slug}` (layer-1 collections, scoped per parent).
     * Optional during the backfill window and on private/primary rows.
     */
    slug: z.string().max(60).optional(),
    /**
     * Public-read flag. `undefined` = members-only (default); a timestamp
     * = publicly readable since that moment. Settable only on layer-0
     * Groups and standalone layer-1 Collections by an admin; the entity
     * layer rejects primaries, implicit groups, group-rooted children,
     * and stacks. Inherited DOWN the parent chain — a child of a published
     * Group is itself publicly readable without its own `publishedAt`.
     */
    publishedAt: z.string().datetime().optional(),
    pinnedAt: z.string().datetime().optional(),
    archivedAt: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime().optional(),
    deletedAt: z.string().datetime().optional(),
});
// ---------------------------------------------------------------------------
// Collection Member Schema
// ---------------------------------------------------------------------------
export const CollectionMemberSchema = z.object({
    id: prefixedIdSchema("collectionMember"),
    collectionId: prefixedIdSchema("collection"),
    accountId: z.string(),
    role: CollectionMemberRoleSchema,
    mutedAt: z.string().datetime().optional(),
    joinedAt: z.string().datetime(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime().optional(),
});
// ---------------------------------------------------------------------------
// Implicit Group auto-naming
// ---------------------------------------------------------------------------
// Format used when the share flow mints (or reuses) an implicit Group. The
// name reflects the participants in the same shape iMessage shows for a DM
// title: "Alice", "Alice & Bob", "Alice, Bob & Carol", etc. For >= 5 members
// we cap at three explicit names and add "+N" so the UI doesn't grow without
// bound.
//
// The caller is expected to pass display names — usernames when available,
// falling back to email local-parts. Empty strings and undefined entries are
// filtered out so a half-resolved member list still produces a sensible
// title. Returns "Untitled group" if every name is empty/missing.
const IMPLICIT_GROUP_FALLBACK_NAME = "Untitled group";
const IMPLICIT_GROUP_MAX_EXPLICIT_NAMES = 3;
export function autoNameFromMembers(memberDisplayNames) {
    const names = memberDisplayNames.map((n) => n?.trim()).filter((n) => !!n);
    if (names.length === 0)
        return IMPLICIT_GROUP_FALLBACK_NAME;
    if (names.length === 1)
        return names[0];
    if (names.length === 2)
        return `${names[0]} & ${names[1]}`;
    if (names.length === 3)
        return `${names[0]}, ${names[1]} & ${names[2]}`;
    if (names.length === 4)
        return `${names[0]}, ${names[1]}, ${names[2]} & ${names[3]}`;
    // 5+: list the first 3, then "+N" for the rest.
    const head = names.slice(0, IMPLICIT_GROUP_MAX_EXPLICIT_NAMES).join(", ");
    const remaining = names.length - IMPLICIT_GROUP_MAX_EXPLICIT_NAMES;
    return `${head} +${remaining}`;
}
